import { clampImageWidth, MIN_IMAGE_WIDTH } from '@/components/content/tiptap/imageResize';

/**
 * Vanilla ProseMirror NodeView for images with drag-to-reposition and
 * drag-to-resize.
 *
 * Why vanilla (not a Vue component): images in this app are lazy-loaded by
 * setting `img.src` directly on the DOM element (see lib/imageRenderUtils +
 * CEContentEditorTipTap.setImagesSrc). A Vue NodeView would re-render the
 * `<img>` from `node.attrs.src` (which is empty for saved images) and clobber
 * the lazy-loaded src with `src="undefined"`. A plain DOM `<img>` lets the
 * existing lazy-load mechanism populate `src` exactly like the base node did.
 */
const createImageNodeView =
  () =>
  ({ node, editor, getPos }) => {
    const dom = document.createElement('span');
    dom.className = 'image-resize-view';
    // The wrapper is the ProseMirror drag handle for repositioning the node.
    dom.setAttribute('data-drag-handle', '');

    // The <img> must NOT be natively draggable: the browser's image drag would
    // fire alongside ProseMirror's node drag and duplicate the image on drop.
    const img = document.createElement('img');
    img.draggable = false;

    const handle = document.createElement('span');
    handle.className = 'image-resize-handle';
    handle.title = 'Drag to resize';

    /** Apply node attributes onto the DOM image (only set what's present). */
    const applyAttributes = (currentNode) => {
      const { src, alt, title, layout, width } = currentNode.attrs;

      // Only set src when present so the lazy-loader can populate saved images.
      if (src) {
        img.setAttribute('src', src);
      } else {
        img.removeAttribute('src');
      }

      img.alt = alt ?? '';
      if (title) img.setAttribute('title', title);
      else img.removeAttribute('title');

      // Legacy images carry no layout; leave the attribute off entirely so they
      // keep the pre-feature rendering instead of adopting the `inline` style.
      if (layout) dom.setAttribute('data-layout', layout);
      else dom.removeAttribute('data-layout');

      if (width) img.style.width = `${width}px`;
      else img.style.removeProperty('width');
    };

    const setResizable = (resizable) => {
      dom.classList.toggle('is-resizable', resizable);
      handle.style.display = resizable ? '' : 'none';
    };

    const startResize = (event) => {
      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startWidth = img.offsetWidth || MIN_IMAGE_WIDTH;
      // Clamp to the editor content width so the image can't overflow.
      const maxWidth = editor.view.dom.clientWidth || Infinity;

      const onMove = (moveEvent) => {
        img.style.width = `${clampImageWidth(startWidth, moveEvent.clientX - startX, maxWidth)}px`;
      };

      const onUp = () => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);

        const finalWidth = parseInt(img.style.width, 10);
        if (!Number.isNaN(finalWidth) && typeof getPos === 'function') {
          editor
            .chain()
            .command(({ tr }) => {
              tr.setNodeMarkup(getPos(), undefined, { ...node.attrs, width: finalWidth });
              return true;
            })
            .run();
        }
      };

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
    };

    // The resize handle must not start a node drag.
    handle.addEventListener('pointerdown', startResize);
    handle.addEventListener('mousedown', (event) => event.stopPropagation());
    handle.addEventListener('dragstart', (event) => event.preventDefault());

    applyAttributes(node);
    dom.appendChild(img);
    dom.appendChild(handle);
    setResizable(false);

    return {
      dom,
      update(updatedNode) {
        if (updatedNode.type !== node.type) return false;
        // eslint-disable-next-line no-param-reassign
        node = updatedNode;
        applyAttributes(updatedNode);
        return true;
      },
      selectNode() {
        if (editor.isEditable) setResizable(true);
      },
      deselectNode() {
        setResizable(false);
      },
      // Image is a leaf node with no editable content, and the lazy-loader
      // mutates img src/class/style directly on the DOM. Ignore all such DOM
      // mutations so ProseMirror doesn't re-parse and wipe them.
      ignoreMutation() {
        return true;
      }
    };
  };

export default createImageNodeView;
